/**
 * Sanitize a possibly-incomplete markdown string for display during the
 * IN-PROGRESS (still streaming) state of an assistant message.
 *
 * While the model types a message character-by-character, the raw markdown
 * syntax of a token that has not finished generating is briefly visible — you
 * literally see `[label](http...` before it snaps into a rendered link. This
 * helper hides that raw syntax for the trailing, still-incomplete token so the
 * stream only ever reveals plain label text (or nothing) until the token is
 * fully formed, at which point the untouched markdown renders normally.
 *
 * It deliberately only neutralizes the trailing/incomplete region. Any
 * fully-formed markdown earlier in the string passes through unchanged, so the
 * caller MUST stop applying this once the message is finalized and render the
 * complete markdown verbatim.
 *
 * It also hides what the settle will DELETE rather than merely re-render: every
 * source note and citation marker (`holdBackBracketRuns`, which leaves real
 * links alone), and a marker that survives on the wire, which is rendered as
 * plain text instead of something markdown may resolve into a link
 * (`escapeStreamingMarkers`).
 *
 * Scope is intentionally narrow (it is NOT a markdown parser): incomplete
 * inline links and images, an open source note, citation markers, plus a
 * dangling inline-code or emphasis marker at the very tail.
 */
export function sanitizeStreamingMarkdown(partial: string): string {
  if (!partial) return partial;

  let out = partial;
  out = holdBackBracketRuns(out);
  out = stripIncompleteLink(out);
  out = neutralizeTrailingInlineCode(out);
  out = neutralizeTrailingEmphasis(out);
  return out;
}

/**
 * Render the streamed text as PROSE ONLY: drop every `[` run the settle is
 * going to take away, closed or open, on any line.
 *
 * A SOURCE NOTE is a bracket run holding a letter
 * (`[Regulation, Annex, p. 16; Claims Powerhouse, A534:C534]`) and the landed
 * answer deletes it: `place_markers` turns it into the `[n]` chip markers
 * (`services/warhol/agents/primary/reply_text.py`, `NOTE`). A numeric run is
 * the same shape and is renumbered against a harvest that does not exist until
 * the turn ends, so a streamed `[1]` can land as `[7]`, or as nothing.
 *
 * Holding back only the UNTERMINATED run was not enough, and the gate measured
 * exactly what that costs: the note closes within one frame, so the reader is
 * shown `[Baby Vision, p. 1]` in full and then watches the settle delete it —
 * the jump this whole path exists to prevent. Both shapes are guesses about a
 * text that is still being rewritten, so while a row streams neither is shown.
 *
 * A real markdown link is NOT a guess and is left alone: `[label](url)` renders
 * as its label then and after the settle, so removing it would take words away
 * and put them back. That is the whole of the `(?!\()` — a run followed by
 * `(` is a link, and everything else is a note or a marker.
 *
 * One line is the bound, matching `NOTE`'s `[^\]\n]*`: a `[` never swallows a
 * line break. An unclosed run is matched only when it runs to the end of its
 * line, so a `[label]` whose `(url)` has not arrived yet cannot be mistaken for
 * an unclosed note and half-eaten.
 *
 * An already-escaped `\[1\]` is left alone, the same way `escapeStreamingMarkers`
 * used to leave it: it is literal text the model chose, not a bracket run.
 * Checked on the match OFFSET rather than by consuming the character in front of
 * the run, so an adjacent pair (`[1][2]`) is not left half-eaten — the same trap
 * that alternation fell into.
 */
function holdBackBracketRuns(s: string): string {
  return s.replace(
    /!?\[[^\]\n]*\](?!\()|!?\[[^\]\n]*(?=\n|$)/g,
    (match: string, offset: number) => (s[offset - 1] === "\\" ? match : ""),
  );
}

/**
 * Replace a link whose url is still being typed with just its label text —
 * `[label](partialUrl` becomes `label`, so the reader sees the words and never
 * the half-written target.
 *
 * This is the ONE bracket shape `holdBackBracketRuns` deliberately leaves
 * behind: a run followed by `(` is a link the settle keeps, not a note or a
 * marker it deletes, so its label must stay on screen. A COMPLETE link
 * (`[a](b)`) cannot match — the pattern ends at a string with no closing paren,
 * and the `[^[\]\n]` class keeps each match inside one bracket pair on one line.
 *
 * The two shapes the old version also handled — a tail `[label]` with no paren
 * yet, and a tail `[partial label` — are gone: `holdBackBracketRuns` removes
 * both before this runs, because neither can be told from a source note and the
 * note is what the settle deletes.
 */
function stripIncompleteLink(s: string): string {
  const openUrl = /!?\[([^[\]\n]*)\]\([^()\n]*$/;
  if (openUrl.test(s)) return s.replace(openUrl, "$1");
  return s;
}

/**
 * Remove a single unmatched inline-code backtick so a half-typed `` `code ``
 * span shows the code text plainly instead of a stray leading backtick. Fenced
 * code blocks (runs of backticks) are left alone — we only strip a lone
 * backtick that is not adjacent to another backtick.
 */
function neutralizeTrailingInlineCode(s: string): string {
  const backticks = (s.match(/`/g) ?? []).length;
  if (backticks % 2 === 0) return s;

  const idx = s.lastIndexOf("`");
  if (s[idx - 1] === "`" || s[idx + 1] === "`") return s;
  return s.slice(0, idx) + s.slice(idx + 1);
}

/**
 * Strip a dangling emphasis marker (`**`, `__`, `*`, `_`) when its count is odd
 * (an opener with no matching closer), so a half-typed `**bold` shows `bold`
 * rather than leading asterisks. Double markers are handled before single ones
 * so the two `*` of a `**` pair are not mistaken for unmatched single markers.
 */
function neutralizeTrailingEmphasis(s: string): string {
  let out = s;
  for (const marker of ["**", "__", "*", "_"]) {
    const count = out.split(marker).length - 1;
    if (count % 2 === 1) {
      const idx = out.lastIndexOf(marker);
      out = out.slice(0, idx) + out.slice(idx + marker.length);
    }
  }
  return out;
}
